import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { unwrapConnection } from "@utils/utils";
import clsx from "clsx";
import React, { useEffect } from "react";
import { useState } from "react";

import { Badge, Tooltip } from "@fronterahealth/frontera-ui-components";

import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
import { TruncateWithTooltip } from "@components/TruncateWithTooltip";
import { MomentRow } from "@pages/VideoSessionsV2/components/Moments/VideoMoments/components/MomentRow";
import { TargetMoment, useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface TargetRowProps {
  target: TargetMoment;
  onClick: (startTime: number, endTime: number, momentId: string) => void;
  expandAll?: boolean;
}

export const TargetRow: React.FC<TargetRowProps> = ({ target, onClick, expandAll }) => {
  const [isExpanded, setIsExpanded] = useState(expandAll || false);
  const targetMoments = unwrapConnection(target.mediatargetpredictionSet) || [];
  const Icon = target.status ? getStatusIcon(target.status) : React.Fragment;

  const { activeMomentId, activeMomentTargetId } = useVideoSessionData();

  useEffect(() => {
    setIsExpanded(expandAll || false);
  }, [expandAll]);

  useEffect(() => {
    if (activeMomentTargetId === target.id) {
      setIsExpanded(true);
    }
  }, [activeMomentTargetId, target.id]);

  return (
    <div className="border-t border-limestone-200 overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-3 hover:bg-limestone-50 transition-colors cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ChevronRightIcon
            className={clsx(
              "w-4 h-4 text-limestone-400 transition-transform duration-200 shrink-0",
              isExpanded && "rotate-90",
            )}
          />
          <TruncateWithTooltip>
            <span className="text-sm text-limestone-900 flex-1">{target.name || "Unnamed Target"}</span>
          </TruncateWithTooltip>
          <span className="px-2 py-0.5 text-xs font-medium text-limestone-600 bg-limestone-100 rounded-full shrink-0">
            {targetMoments.length}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {target.status && (
            <Tooltip text={target.status} showInfoIcon={false}>
              <Badge
                text={
                  <div className="flex flex-row gap-1">
                    <Icon className="h-3 w-3" />
                  </div>
                }
                className="text-sm"
                appearance={getStatusAppearance(target.status)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className={clsx("overflow-hidden transition-all duration-300", isExpanded ? "max-h-96" : "max-h-0")}>
        {targetMoments.length > 0 ? (
          <div className="px-2 pb-2">
            {targetMoments.map(
              (moment) =>
                moment && (
                  <MomentRow
                    key={moment.id}
                    moment={moment}
                    isActive={moment.id === activeMomentId}
                    onClick={() => onClick(moment.startTime, moment.endTime, moment.id)}
                  />
                ),
            )}
          </div>
        ) : (
          <div className="px-4 py-2 text-sm text-limestone-400 italic">No moments for this target</div>
        )}
      </div>
    </div>
  );
};
